/**
 * AICostBadge — pre-flight credit-cost preview.
 *
 * Renders a tiny inline pill like "🪄 3 credits · 47 left" that admins &
 * builders can drop next to any AI-triggering button so members never
 * hit an unexpected 402 mid-flow. Reads /me/ai-credits once on mount and
 * caches the response for the session (per-instance so newer instances
 * see fresh data). Admins/moderators see "Bypass" instead of a cost.
 *
 * Usage:
 *   <AICostBadge tool="calculator.ai_analysis" />
 *   <AICostBadge tool="ambassador.basic_question" compact />
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Sparkles, ShieldCheck } from 'lucide-react';

// Lightweight session-level cache so the widget on every AI button
// doesn't fire a separate /me/ai-credits request. Refreshes on demand.
let _cache = null;
let _promise = null;

async function fetchSnapshot(API) {
  if (_cache) return _cache;
  if (_promise) return _promise;
  _promise = axios.get(`${API}/me/ai-credits`)
    .then((r) => { _cache = r.data; return _cache; })
    .catch(() => { _cache = null; return null; })
    .finally(() => { _promise = null; });
  return _promise;
}

// Trigger a refresh (e.g. after an AI call actually succeeded/failed).
export function invalidateAICostCache() { _cache = null; }

export default function AICostBadge({ tool, compact = false, className = '' }) {
  const { API } = useContext(AuthContext);
  const [snap, setSnap] = useState(_cache);

  useEffect(() => {
    let cancel = false;
    fetchSnapshot(API).then((s) => { if (!cancel) setSnap(s); });
    return () => { cancel = true; };
  }, [API]);

  if (!snap) return null;

  const cost      = snap?.tool_costs?.[tool];
  const remaining = snap?.remaining ?? 0;
  const bypass    = snap?.bypass;

  if (bypass) {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full bg-indigo-100 text-indigo-700 px-1.5 py-0.5 text-[10px] font-bold ${className}`}
        title="Admin/moderator — you don't consume the member credit pool"
        data-testid={`ai-cost-badge-${tool}`}
      >
        <ShieldCheck className="w-2.5 h-2.5" /> Bypass
      </span>
    );
  }
  if (cost == null) return null;

  // Red when the operation is unaffordable; amber when it would drop
  // the user below 25% of allowance; otherwise green-ish.
  const insufficient = cost > remaining;
  const nearZero     = !insufficient && (remaining - cost) < Math.max(3, Math.floor((snap.allowance || 0) * 0.25));
  const cls = insufficient
    ? 'bg-red-100 text-red-700'
    : nearZero
      ? 'bg-amber-100 text-amber-700'
      : 'bg-emerald-100 text-emerald-700';

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full ${cls} px-1.5 py-0.5 text-[10px] font-bold whitespace-nowrap ${className}`}
      title={insufficient
        ? `You need ${cost} credits but only have ${remaining}.`
        : `Costs ${cost} credit${cost === 1 ? '' : 's'} · ${remaining} left this month`}
      data-testid={`ai-cost-badge-${tool}`}
    >
      <Sparkles className="w-2.5 h-2.5" />
      {compact ? `${cost}cr` : `${cost} credit${cost === 1 ? '' : 's'} · ${remaining} left`}
    </span>
  );
}
